import React, {
  ReactElement,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import isEqual from 'lodash-es/isEqual';
import { Jurisdiction } from '@enview/interface/types/Jurisdiction';
import './Selector.scss';
import { useDispatch, useSelector } from 'react-redux';
import MultiSelectForm from '../Forms/MultiSelectForm';
import { State } from '../../../dux/@types';
import { getSupportedJurisdictions } from '../../../dux';
import {
  getLegislaturesOptions,
  filterUSJurisdictions,
} from '../../../helpers/JurisdictionSelectionHelper';

type JurisdictionSelectorProps = {
  jurisdictions?: Jurisdiction[];
  searchChanged?: Dispatch<SetStateAction<boolean>>;
  selectedFilters: string[];
  onSelectionChange: (filter: string[]) => void;
  saveButtonProps?: {
    label: string;
    type: 'button' | 'submit' | 'reset';
    onClick: () => void;
  };
};

const JurisdictionSelector = (props: JurisdictionSelectorProps): ReactElement => {
  const {
    jurisdictions,
    searchChanged,
    selectedFilters,
    onSelectionChange,
    saveButtonProps,
  } = props;
  const supportedJurisdictions = useSelector(
    (state: State) => state.account.supportedJurisdictions,
  );
  const [errorMessage, setErrorMessage] = useState<string>();

  const legislaturesOptions =
    (supportedJurisdictions &&
      getLegislaturesOptions(
        jurisdictions || supportedJurisdictions,
        supportedJurisdictions,
      )) ||
    [];
  const [jurisdictionFilters, setJurisdictionFilters] = useState(selectedFilters);
  const dispatch = useDispatch();

  const handleJurisdictionFiltersChange = (
    updatedJurisdictionFilters: string[],
  ): void => {
    if (isEqual(updatedJurisdictionFilters, jurisdictionFilters)) return;
    setJurisdictionFilters(updatedJurisdictionFilters);
    onSelectionChange(updatedJurisdictionFilters);
    if (searchChanged) searchChanged(true);
  };

  const handleClick = (): void => {
    if (jurisdictionFilters.length === 0) {
      setErrorMessage('Please select at least one jurisdiction.');
      return;
    }
    if (saveButtonProps) {
      setErrorMessage(undefined);
      saveButtonProps.onClick();
    }
  };

  useEffect(() => {
    if (!supportedJurisdictions) {
      dispatch(getSupportedJurisdictions());
    }
  }, [supportedJurisdictions]);

  return (
    <div className="jurisdiction-selector">
      <MultiSelectForm
        currentSelection={jurisdictionFilters}
        options={legislaturesOptions}
        selectAllFilterFunction={filterUSJurisdictions}
        selectAllLabel="Select All U.S. Jurisdictions"
        showSelectAll
        updateSelection={handleJurisdictionFiltersChange}
      />
      {errorMessage ? <div className="error">{errorMessage}</div> : <></>}
      {saveButtonProps && (
        <button
          className="btn btn-action"
          onClick={handleClick}
          type={saveButtonProps.type}
        >
          {saveButtonProps.label}
        </button>
      )}
    </div>
  );
};

JurisdictionSelector.defaultProps = {
  labelUnsupported: false,
};

export default JurisdictionSelector;
